/**
 * Design tokens — the single source of truth for the MechaniX look.
 *
 * Nothing in the app should hardcode a colour, radius, shadow, or animation
 * duration. Both the MUI theme (src/theme/index.ts) and the motion primitives
 * (src/motion/) read from here, so the two never drift apart.
 */

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

/** Warm neutral ramp. Greys do most of the work; the accent is used sparingly. */
export const grey = {
  0: '#FFFFFF',
  50: '#F8F5F3',
  100: '#F4F0EE',
  200: '#E6E2DF',
  300: '#D5D0CC',
  400: '#A8A49F',
  500: '#605A54',
  600: '#4C4742',
  700: '#3B3734',
  800: '#292622',
  900: '#1B1815',
  950: '#110F0D',
} as const;

/** One confident accent, with hover/active steps and tinted backgrounds. */
export const accent = {
  50: '#ECF6FF',
  100: '#DAEDFF',
  200: '#BCDEFF',
  300: '#93C7FF',
  400: '#65ADFF',
  500: '#3589ED', // chart marks; too light for white text
  600: '#1E76D8', // primary in light mode (4.53:1 with white)
  700: '#195DAA',
  800: '#164781',
  900: '#13335A',
} as const;

/** Secondary accent, reserved for highlights that must not read as primary. */
export const teal = {
  300: '#5EE7D5',
  400: '#2FD5BE',
  500: '#14B8A2',
  600: '#0E9384',
} as const;

/**
 * Garage colours for the public marketing pages: Old Glory red for calls to
 * action and highlights, safety yellow for hazard striping only. Admin screens
 * keep the blue accent. On dark surfaces, text uses `glory[400]` (5.2:1 on
 * grey 950); `glory[600]` carries white text (6.6:1).
 */
export const glory = {
  300: '#F0919A',
  400: '#E25563',
  500: '#C8303F',
  600: '#B22234',
  700: '#8E1B29',
  800: '#6B1420',
} as const;

export const hazard = {
  400: '#FFCF33',
  500: '#F5B800',
} as const;

/**
 * Semantic colours, per colour mode. In both modes `light` is the tinted
 * background, `main` the colour itself and `dark` the strong text on that
 * tint — so in dark mode `light` is the darkest step and `dark` the lightest.
 * `neutral` is not a hue of its own: it is built from the grey ramp.
 */
export const semantic = {
  light: {
    success: { light: '#DCF7E1', main: '#36A558', dark: '#21763C' },
    warning: { light: '#FFEBD2', main: '#C07C00', dark: '#8A5700' },
    danger: { light: '#FFE5E1', main: '#D8625C', dark: '#9C433F' },
    info: { light: '#DBF1FF', main: '#3690E3', dark: '#2266A4' },
    neutral: { light: grey[100], main: grey[400], dark: grey[500] },
  },
  dark: {
    success: { light: '#14361D', main: '#69C27E', dark: '#95DFA4' },
    warning: { light: '#3F2903', main: '#DA9E3F', dark: '#F5C076' },
    danger: { light: '#47211E', main: '#F28881', dark: '#FFAEA6' },
    info: { light: '#142F4B', main: '#67B0F9', dark: '#92D0FF' },
    neutral: { light: grey[950], main: grey[600], dark: grey[400] },
  },
} as const;

export type SemanticTone = keyof (typeof semantic)['light'];

// ---------------------------------------------------------------------------
// Shape, spacing, elevation
// ---------------------------------------------------------------------------

export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
  /** Buttons, inputs and icon buttons. */
  control: 10,
  /** Cards and table containers. */
  card: 14,
} as const;

/** Height of a default-size button, input or icon button. */
export const controlHeight = 40;

/** MUI's spacing unit; theme.spacing(n) === n * 8px. */
export const spacingUnit = 8;

/**
 * Soft, low-contrast elevation. Shadows carry depth so borders can stay
 * hairline-thin — the main reason stock MUI reads as "flat grey boxes".
 */
export const shadowLight = {
  xs: '0 1px 2px rgba(16, 24, 40, 0.05)',
  sm: '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
  md: '0 4px 8px -2px rgba(16, 24, 40, 0.08), 0 2px 4px -2px rgba(16, 24, 40, 0.04)',
  lg: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)',
  xl: '0 20px 24px -4px rgba(16, 24, 40, 0.10), 0 8px 8px -4px rgba(16, 24, 40, 0.04)',
  '2xl': '0 32px 64px -12px rgba(16, 24, 40, 0.16)',
} as const;

export const shadowDark = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.30)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.40), 0 1px 2px rgba(0, 0, 0, 0.24)',
  md: '0 4px 8px -2px rgba(0, 0, 0, 0.44), 0 2px 4px -2px rgba(0, 0, 0, 0.28)',
  lg: '0 12px 16px -4px rgba(0, 0, 0, 0.48), 0 4px 6px -2px rgba(0, 0, 0, 0.30)',
  xl: '0 20px 24px -4px rgba(0, 0, 0, 0.52), 0 8px 8px -4px rgba(0, 0, 0, 0.32)',
  '2xl': '0 32px 64px -12px rgba(0, 0, 0, 0.62)',
} as const;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const fontFamily = [
  '"Inter Variable"',
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ');

/**
 * Condensed display face for the public garage pages: Barlow Condensed, drawn
 * after US highway signage. Headlines, labels and numbers only; body copy
 * stays Inter. Self-hosted, imported once in src/index.tsx.
 */
export const fontFamilyDisplay = [
  '"Barlow Condensed"',
  '"Arial Narrow"',
  'Impact',
  'sans-serif',
].join(', ');

export const fontFamilyMono = [
  '"SF Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(', ');

/** Large display sizes need negative tracking or they read loose and cheap. */
export const tracking = {
  display: '-0.032em',
  heading: '-0.018em',
  body: '0em',
  overline: '0.08em',
} as const;

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

/** Seconds — Motion's unit. Multiply by 1000 for MUI/CSS. */
export const duration = {
  instant: 0.12,
  fast: 0.18,
  base: 0.28,
  slow: 0.45,
  slower: 0.7,
} as const;

/** Cubic-bezier curves as Motion tuples. */
export const ease = {
  /** Default for entrances — decelerates into place. */
  out: [0.16, 1, 0.3, 1] as const,
  /** Symmetric, for things that move both ways (dialogs, accordions). */
  inOut: [0.65, 0, 0.35, 1] as const,
  /** Slight overshoot; use for press/pop feedback only. */
  emphasis: [0.34, 1.56, 0.64, 1] as const,
};

export const spring = {
  /** Snappy press/hover feedback. */
  press: { type: 'spring', stiffness: 420, damping: 30 } as const,
  /** Softer, for layout shifts. */
  gentle: { type: 'spring', stiffness: 200, damping: 28 } as const,
};

/** How far elements travel on entrance, in px. Small = tasteful. */
export const travel = {
  sm: 8,
  md: 16,
  lg: 28,
} as const;

/** Delay between staggered children, in seconds. */
export const stagger = {
  tight: 0.04,
  base: 0.07,
  loose: 0.12,
} as const;

/** Turns a Motion duration into the `Xms` string MUI transitions expect. */
export const ms = (seconds: number) => `${Math.round(seconds * 1000)}ms`;

/** Turns an ease tuple into a CSS cubic-bezier() string. */
export const cssEase = (curve: readonly number[]) => `cubic-bezier(${curve.join(', ')})`;
